import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import type { Editor } from "@tiptap/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { FormModal } from "../dialog";

export interface InsertLinkDialogProps {
    open: boolean;
    editor: Editor;
    onClose: () => void;
}

export function InsertLinkDialog({
    open,
    editor,
    onClose,
}: InsertLinkDialogProps) {
    const { t } = useTranslation();
    const [url, setUrl] = useState("");

    useEffect(() => {
        if (!open || !editor) return;
        const { from, to } = editor.state.selection;
        const hasTextSelected = from !== to;
        const linkAttrs = editor.getAttributes("link");
        const linkActive = editor.isActive("link");

        let nextUrl = "";
        if (hasTextSelected || linkActive) {
            nextUrl = linkAttrs?.href || "";
        }

        // Reflect the editor selection into local state so the dialog shows the current link target.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setUrl(nextUrl);
    }, [open, editor]);

    function handleSubmit(event: React.FormEvent) {
        event.preventDefault();

        if (!url.trim() || !editor) return;

        // Apply or update link on selected text or existing link
        editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url })
            .run();

        handleClose();
    }

    function handleClose() {
        setUrl("");
        onClose();
    }

    return (
        <FormModal
            open={open}
            title={t("modals.insert-link.title")}
            onSubmit={handleSubmit}
            onCancel={handleClose}
            submitText={t("insert")}
            confirmDisabled={!url.trim()}
        >
            <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                    autoFocus
                    label={t("form.fields.url.label")}
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    fullWidth
                    required
                />
            </Stack>
        </FormModal>
    );
}
